import { FC, ReactElement } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { RootState } from "../app/Store";
import { selectSiteId } from "../sites/SiteStore";
import getIcons from "../../SolarGikLib/icons/Icons";
import { GeneralIcons, IconCategory } from "../../SolarGikLib/icons/IconsModels";
import { isSitePageVisible } from "./NavigationUtils";
import { SitePage } from "../../pages/PageNames";
import Layout from "./Layout";
import { isAiChatEnabledForUser } from "../chat/components/chatUtils";
import OAuthResponseDialog from "../o_auth/OAuthResponseDialog";
import SitePageNotice from "./SitePageNotice";
import { UserAccessType } from "../user/Models";

interface ContentPageRouterProps {
  children: ReactElement;
  backgroundWindowStyle?: React.CSSProperties;
  layoutStyle?: React.CSSProperties;
  scrollableLayout?: boolean;
  showShadowForScrollableLayout?: boolean;
}

const ContentPageRouter: FC<ContentPageRouterProps> = ({
  children,
  backgroundWindowStyle,
  layoutStyle,
  scrollableLayout,
  showShadowForScrollableLayout,
}) => {
  const location = useLocation();
  const siteIdFromStore = useSelector(selectSiteId);
  const { currentSite: siteCodeFromRoute = "" } = useParams<{ currentSite: string }>();
  const multiSitesMetadata = useSelector((state: RootState) => state.multiSitesMetadata);
  const multiSitesFeatureFlags = useSelector((state: RootState) => state.featureFlags.sitesFeatureFlags);
  const multiSitesProducts = useSelector((state: RootState) => state.purchasedProducts?.productsBySite);
  const userAccessType = useSelector((state: RootState) => state.user.userType);
  const siteIdFromCode = multiSitesMetadata.siteCodeToIdMap[siteCodeFromRoute];

  if (siteCodeFromRoute && !siteIdFromCode) {
    return (
      <SitePageNotice
        title={`404 - no such site "${siteCodeFromRoute}"`}
        message="Check the site code in the URL and try again."
        fullPage
      />
    );
  }

  if (siteCodeFromRoute && siteIdFromCode && siteIdFromCode === siteIdFromStore) {
    const routePage = location.pathname.split("/")[2]?.toLowerCase();
    const pageName = Object.values(SitePage).find((page) => page.toLowerCase() === routePage);
    const isPageAvailable =
      pageName !== undefined &&
      (userAccessType >= UserAccessType.Engineer ||
        isSitePageVisible(pageName, {
          userAccessType,
          siteFeatureFlags: multiSitesFeatureFlags[siteIdFromCode] ?? [],
          siteMetadata: multiSitesMetadata.sites[siteIdFromCode],
          siteProducts: multiSitesProducts?.[siteIdFromCode] ?? [],
        }));
    const isAiChatEnabled = isAiChatEnabledForUser(
      [siteIdFromStore],
      multiSitesFeatureFlags,
      userAccessType
    );
    return (
      <Layout
        backgroundWindowStyle={backgroundWindowStyle}
        layoutStyle={layoutStyle}
        scrollableLayout={scrollableLayout}
        showShadowForScrollableLayout={showShadowForScrollableLayout}
        isAiChatEnabled={isAiChatEnabled}
      >
        <>
          <OAuthResponseDialog />
          {isPageAvailable ? (
            children
          ) : (
            <SitePageNotice
              title="This feature isn’t available for your site"
              message="Contact support for more details."
            />
          )}
        </>
      </Layout>
    );
  }

  const SolargikIcon = getIcons(GeneralIcons.SolarGikLogo, IconCategory.General);

  return (
    <div className="third-height">
      <SolargikIcon fontSize={"large"} />
    </div>
  );
};

export default ContentPageRouter;
